import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, delay, filter, map, retryWhen, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompanyRepositoryService } from '@core/repositories/admin/company/company-repository.service';
import { ICompanyModel } from '@core/models/admin/company/company.model';
import { CookieService } from 'ngx-cookie-service';
import { GlobalHelper } from '@core/helpers/global.helper';
import { environment } from 'src/environments/environment';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { Store } from '@ngrx/store';
import { selectLoginInfo } from 'src/app/store/login/login.selectors';
import { IAccountInfo } from '@core/models/admin/account/accountInfo.model';
import { AccountRepositoryService } from '@core/repositories/admin/account/account-repository.service';
import { IUserModel } from '@core/models/shared/user.model';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IParamsPlant } from '@shared/interfaces/parameter-plant.interface';
import { IdbParameterDataEntry } from 'src/app/idb/AppDB';

@Injectable({
  providedIn: 'root',
})
export class UpdateCommonDataService {
  private helper: GlobalHelper;
  private readonly _baseUrl: string = environment.APIM + environment.newOrderAPI;

  private companyData: BehaviorSubject<ICompanyModel> = new BehaviorSubject<ICompanyModel>({} as any);
  readonly companyData$: Observable<ICompanyModel> = this.companyData.asObservable().pipe(shareReplay(1));

  private profileData: BehaviorSubject<IUserModel> = new BehaviorSubject<IUserModel>({} as any);
  readonly profileData$: Observable<IUserModel> = this.profileData.asObservable().pipe(shareReplay(1));

  constructor(
    private cookieService: CookieService,
    private companyRepository: CompanyRepositoryService,
    private accountRepository: AccountRepositoryService,
    private idb: ManageIdbService,
    private http: HttpClient,
    private store: Store,
  ) {}

  public getUserData(): IUserModel {
    return this.profileData.getValue();
  }

  public UpdateCompanyData(): void {
    setTimeout(() => {
      const request = {} as ICompanyModel;
      request.CompanyGuid = this.cookieService.get(environment.CompanyGuid).toUpperCase();

      if (request.CompanyGuid != undefined && request.CompanyGuid != '00000000-0000-0000-0000-000000000000') {
        this.companyRepository
          .companyinfo(request)
          .pipe(retryWhen((errors) => errors.pipe(take(this.helper.MaxRetries), delay(this.helper.TimeDelay))))
          .subscribe(
            (response: ICompanyModel) => {
              this.idb.saveCompanyInfo(response);
              this.companyData.next(response);
            },
            () => {
              this.companyData.next(null);
            },
          );
      }
    }, 5);
  }

  UpdateProfileData(forceUpdate: boolean = false): void {
    from(this.idb.select(IdbCatalogName.AccountInfo) as Promise<IUserModel>)
      .pipe(take(1))
      .subscribe((userIdb) => {
        if (userIdb && !forceUpdate) {
          this.profileData.next(userIdb);
          return;
        }
        this.store
          .select(selectLoginInfo)
          .pipe(
            filter((loginInfo) => !!loginInfo),
            take(1),
          )
          .subscribe((loginInfo) => {
            const userGuid = loginInfo.UserGuid?.toUpperCase() || '';
            if (!userGuid || userGuid === '00000000-0000-0000-0000-000000000000') {
              return;
            }
            this.accountRepository
              .accountinfo({ UserGuid: userGuid } as IAccountInfo)
              .pipe(take(1))
              .subscribe((u: IUserModel) => {
                this.idb.saveAccountInfo(u);
                this.profileData.next(u);
              });
          });
      });
  }

  public NotifyUser(component: IUserModel): void {
    setTimeout(() => {
      this.profileData.next(component);
    }, 5);
  }

  updateParameter(paramName: string): Observable<IParamsPlant> {
    const scopeId = null;
    const parameterScopeTypeId = 5;
    const key = this.buildParameterKey(paramName, scopeId, parameterScopeTypeId);

    return from(this.idb.getParametersData([key])).pipe(
      catchError(() => of([] as IdbParameterDataEntry[])),
      switchMap((parameters) => {
        const cachedParameter = parameters[0];
        if (cachedParameter?.parameterValue !== undefined) {
          return of(this.buildParameterInfo(paramName, cachedParameter.parameterValue));
        }

        return this.getParameterByDetails(paramName, scopeId, parameterScopeTypeId).pipe(
          take(1),
          switchMap((value) => {
            const paramInfo = this.buildParameterInfo(paramName, value);
            const entry: IdbParameterDataEntry = {
              key,
              parameterCode: paramName,
              parameterValue: value || '',
              scopeId,
              parameterScopeTypeId,
            };

            return from(this.idb.saveParametersData([entry])).pipe(
              map(() => paramInfo),
              catchError(() => of(paramInfo)),
            );
          }),
        );
      }),
      tap((paramInfo) => sessionStorage.setItem(paramName, JSON.stringify(paramInfo))),
    );
  }

  private buildParameterKey(parameterCode: string, scopeId: number | null, parameterScopeTypeId: number): string {
    return `${parameterCode}|${scopeId ?? 'null'}|${parameterScopeTypeId}`;
  }

  private buildParameterInfo(paramName: string, value: string): IParamsPlant {
    return {
      parameterCode: paramName,
      parameterDesc: paramName,
      parameterValue: value ? Number(value) : null,
    };
  }

  getParameterByDetails(ParameterCode: string, ScopeId: number, ParameterScopeTypeId: number): Observable<string> {
    const options = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('Ocp-Apim-Subscription-Key', environment.apimSubscriptionKey),
      responseType: 'text' as 'text',
    };

    return this.http.put(
      `${this._baseUrl}Parameter/GetParameterData/`,
      {
        ParameterCode: ParameterCode,
        ScopeId: ScopeId,
        ParameterScopeTypeId: ParameterScopeTypeId,
      },
      options,
    );
  }
}
